/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { loadGuestStep, updateGuestStep, submitGuestBooking } from 'core/guestBooking/guestBookingApi';
import { NsipVisitorSearchResult, StepData, Visitor, VisitorRequest, VisitRequestFormState } from './types';
import {
    createAccessRequest,
    deleteAccessRequest,
    loadAccessRequestForm,
    updateAccessRequestForm,
    uploadAccessRequestAttachment,
    deleteAccessRequestAttachment,
    amendAccessRequestAction,
    uploadAccessRequestVisitorCsv,
    searchNsipVisitors,
} from './accessRequestFormApi';

const emptyVisitRequestState: VisitorRequest = {
    encryptedId: null,
    id: null,
    site: null,
    requester: null,
    approver: null,
    costApproverByPass: false, // Not used for access requests but required by shared VisitorRequest type
    attachments: [],
    status: '',
    currentStep: 0,
    visitors: null,
    dates: [],
    totalHours: 0,
    siteAccess: [],
    additionalComments: '',
    workTypeName: null,
    workTypes: null,
    workDetails: {
        description: null,
        workReference: null,
    },
    declaration: {
        signature: null,
        agreed: false,
    },
};

const initialState = {
    error: null,
    isFetching: false,
    isUpdating: false,
    isCreating: false,
    isAmending: false,
    isUploading: false,
    status: 0,
    visitRequest: emptyVisitRequestState,
    stepData: {
        sites: [],
        openDays: [],
        approvedVisits: [],
        totalHours: 0,
        enabled24_7: false,
    },
    applyToAllVisitors: true,
    lastRequestAmended: null,
    nsipSearchResults: [],
    isSearching: false,
} as VisitRequestFormState;

export const accessRequestFormSlice = createSlice({
    name: 'accessRequestForm',
    initialState,
    reducers: {
        setApplyToAllVisitors: (state, action) => {
            const { payload } = action;
            state.applyToAllVisitors = payload;
        },
        updateVisitors: (state, action) => {
            const { payload } = action;
            state.visitRequest.visitors = payload;
        },
        setLastRequestAmended: (state, action) => {
            const { payload } = action;
            state.lastRequestAmended = payload;
        },
        clearNsipSearchResults: (state) => {
            state.nsipSearchResults = [];
        },
    },
    extraReducers: (builder) => {
        // Create
        builder
            .addCase(createAccessRequest.pending, (state: VisitRequestFormState) => {
                state.isCreating = true;
                state.error = null;
            });
        builder
            .addCase(createAccessRequest.fulfilled, (
                state: VisitRequestFormState,
                action: PayloadAction<{ visitRequest: VisitorRequest, stepData: StepData }>,
            ) => {
                state.isCreating = false;
                state.visitRequest = action.payload.visitRequest;
                state.stepData = action.payload.stepData;
                state.status = 200;
            });
        builder
            .addCase(createAccessRequest.rejected, (
                state: VisitRequestFormState,
                action: object,
            ) => {
                state.isCreating = false;
                state.error = action;
                state.visitRequest = emptyVisitRequestState;
            });

        // Load
        builder
            .addCase(loadAccessRequestForm.pending, (state: VisitRequestFormState) => {
                state.isFetching = true;
                state.error = null;
            });
        builder
            .addCase(loadAccessRequestForm.fulfilled, (
                state: VisitRequestFormState,
                action: PayloadAction<{ visitRequest: VisitorRequest, stepData: StepData }>,
            ) => {
                state.isFetching = false;
                state.visitRequest = action.payload.visitRequest;
                state.stepData = action.payload.stepData;
                state.status = 200;
            });
        builder
            .addCase(loadAccessRequestForm.rejected, (
                state: VisitRequestFormState,
                action: object,
            ) => {
                state.isFetching = false;
                state.error = action;
                state.visitRequest = emptyVisitRequestState;
            });

        // Update
        builder
            .addCase(updateAccessRequestForm.pending, (state: VisitRequestFormState) => {
                state.isUpdating = true;
                state.error = null;
            });
        builder
            .addCase(updateAccessRequestForm.fulfilled, (
                state: VisitRequestFormState,
                action: PayloadAction<{ visitRequest: VisitorRequest, stepData: StepData }>,
            ) => {
                state.isUpdating = false;
                state.visitRequest = action.payload.visitRequest;
                state.stepData = action.payload.stepData;
                state.status = 200;
            });
        builder
            .addCase(updateAccessRequestForm.rejected, (
                state: VisitRequestFormState,
                action: object,
            ) => {
                state.isUpdating = false;
                state.error = action;
            });

        // Upload attachment
        builder
            .addCase(uploadAccessRequestAttachment.pending, (state: VisitRequestFormState) => {
                state.isFetching = true;
                state.error = null;
            });
        builder
            .addCase(uploadAccessRequestAttachment.fulfilled, (
                state: VisitRequestFormState,
                action: PayloadAction<{ message: string, visitRequest: VisitorRequest }>,
            ) => {
                state.isFetching = false;
                state.visitRequest = action.payload.visitRequest;
                state.status = 200;
            });
        builder
            .addCase(uploadAccessRequestAttachment.rejected, (
                state: VisitRequestFormState,
                action: object,
            ) => {
                state.isFetching = false;
                state.error = action;
            });

        // Delete attachment
        builder
            .addCase(deleteAccessRequestAttachment.pending, (state: VisitRequestFormState) => {
                state.isFetching = true;
                state.error = null;
            });
        builder
            .addCase(deleteAccessRequestAttachment.fulfilled, (
                state: VisitRequestFormState,
                action: PayloadAction<{ message: string, visitRequest: VisitorRequest }>,
            ) => {
                state.isFetching = false;
                state.visitRequest = action.payload.visitRequest;
                state.status = 200;
            });
        builder
            .addCase(deleteAccessRequestAttachment.rejected, (
                state: VisitRequestFormState,
                action: object,
            ) => {
                state.isFetching = false;
                state.error = action;
            });

        // Amend action
        builder
            .addCase(amendAccessRequestAction.pending, (state: VisitRequestFormState) => {
                state.isAmending = true;
                state.error = null;
            });
        builder
            .addCase(amendAccessRequestAction.fulfilled, (
                state: VisitRequestFormState,
            ) => {
                state.isAmending = false;
                state.lastRequestAmended = null;
            });
        builder
            .addCase(amendAccessRequestAction.rejected, (
                state: VisitRequestFormState,
                action: object,
            ) => {
                state.isAmending = false;
                state.error = action;
            });

        // Delete
        builder
            .addCase(deleteAccessRequest.pending, (_state: VisitRequestFormState) => {
                // Silent operation
            });
        builder
            .addCase(deleteAccessRequest.fulfilled, (
                state: VisitRequestFormState,
            ) => {
                state.visitRequest = emptyVisitRequestState;
                state.stepData = initialState.stepData;
            });
        builder
            .addCase(deleteAccessRequest.rejected, (
                _state: VisitRequestFormState,
            ) => {
                // Silent operation
            });

        // Visitor CSV upload
        builder
            .addCase(uploadAccessRequestVisitorCsv.pending, (state: VisitRequestFormState) => {
                state.isUploading = true;
                state.error = null;
            });
        builder
            .addCase(uploadAccessRequestVisitorCsv.fulfilled, (
                state: VisitRequestFormState,
                action: PayloadAction<{ visitors: Visitor[] }>,
            ) => {
                state.isUploading = false;

                const newVisitors = [
                    ...state.visitRequest.visitors || [],
                    ...action.payload.visitors,
                ];

                const uniqueVisitors = newVisitors.reduce((acc, current) => {
                    const x = acc.find((item) => item.id === current.id);
                    if (!x) {
                        return acc.concat([current]);
                    }
                    return acc;
                }, [] as Visitor[]);

                state.visitRequest.visitors = uniqueVisitors;
                state.status = 200;
            });
        builder
            .addCase(uploadAccessRequestVisitorCsv.rejected, (
                state: VisitRequestFormState,
                action: object,
            ) => {
                state.isUploading = false;
                state.error = action;
            });

        // NSIP Visitor Search
        builder
            .addCase(searchNsipVisitors.pending, (state: VisitRequestFormState) => {
                state.isSearching = true;
                state.error = null;
            });
        builder
            .addCase(searchNsipVisitors.fulfilled, (
                state: VisitRequestFormState,
                action: PayloadAction<NsipVisitorSearchResult[]>,
            ) => {
                state.isSearching = false;
                state.nsipSearchResults = action.payload;
            });
        builder
            .addCase(searchNsipVisitors.rejected, (
                state: VisitRequestFormState,
                action: object,
            ) => {
                state.isSearching = false;
                state.nsipSearchResults = [];
                state.error = action;
            });
        // Guest booking (token-driven): a one-off link can raise this request
        // type, and the guest endpoints return the same {visitRequest, stepData}
        // shape, so the same store feeds the same step components.
        builder
            .addCase(loadGuestStep.pending, (state: VisitRequestFormState) => {
                state.isFetching = true;
                state.error = null;
            })
            .addCase(loadGuestStep.fulfilled, (
                state: VisitRequestFormState,
                action: PayloadAction<{ visitRequest: VisitorRequest, stepData: StepData }>,
            ) => {
                state.isFetching = false;
                state.visitRequest = action.payload.visitRequest;
                state.stepData = action.payload.stepData;
            })
            .addCase(loadGuestStep.rejected, (state: VisitRequestFormState, action: object) => {
                state.isFetching = false;
                state.error = action;
            });
        builder
            .addCase(updateGuestStep.pending, (state: VisitRequestFormState) => {
                state.isUpdating = true;
                state.error = null;
            })
            .addCase(updateGuestStep.fulfilled, (
                state: VisitRequestFormState,
                action: PayloadAction<{ visitRequest: VisitorRequest, stepData: StepData }>,
            ) => {
                state.isUpdating = false;
                state.visitRequest = action.payload.visitRequest;
                state.stepData = action.payload.stepData;
            })
            .addCase(updateGuestStep.rejected, (state: VisitRequestFormState, action: object) => {
                state.isUpdating = false;
                state.error = action;
            });
        builder
            .addCase(submitGuestBooking.pending, (state: VisitRequestFormState) => {
                state.isUpdating = true;
                state.error = null;
            })
            .addCase(submitGuestBooking.fulfilled, (state: VisitRequestFormState) => {
                state.isUpdating = false;
            })
            .addCase(submitGuestBooking.rejected, (state: VisitRequestFormState, action: object) => {
                state.isUpdating = false;
                state.error = action;
            });
    },
});

export const {
    setApplyToAllVisitors,
    updateVisitors,
    setLastRequestAmended,
    clearNsipSearchResults,
} = accessRequestFormSlice.actions;

export default accessRequestFormSlice.reducer;
